import Cookies from 'js-cookie';
import { toSnakeCase, toCamelCase } from '../utils/helpers';

import { API_BASE_URL } from '../config/api';

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.hasEquipment = Boolean(body?.hasEquipment);
    this.equipmentCount = body?.equipmentCount;
  }
}

const getAuthHeaders = () => {
  const token = Cookies.get('petrofeu_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const parseJsonSafe = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const handleResponse = async (response) => {
  const data = await parseJsonSafe(response);
  if (!response.ok) {
    throw new ApiError(data.message || data.error || 'Requête échouée', {
      status: response.status,
      body: data,
    });
  }
  if (data.success === false) {
    throw new ApiError(data.message || data.error || 'Requête échouée', {
      status: response.status,
      body: data,
    });
  }
  return data;
};

/** Normalizes `{ success, data }` envelopes and legacy raw payloads. */
const unwrapData = (payload, fallback = null) => {
  if (payload == null) return fallback;
  if (Array.isArray(payload)) return payload;
  if (Object.prototype.hasOwnProperty.call(payload, 'data')) return payload.data ?? fallback;
  return payload;
};

const unwrapList = (payload, fallback = []) => {
  const data = unwrapData(payload, fallback);
  return Array.isArray(data) ? data : fallback;
};

const buildQueryUrl = (path, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') searchParams.set(key, value);
  });
  const query = searchParams.toString();
  return query ? `${API_BASE_URL}${path}?${query}` : `${API_BASE_URL}${path}`;
};

export const apiService = {
  // Clients
  async getClients() {
    const response = await fetch(`${API_BASE_URL}/api/clients`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getClient(id) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${id}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async createClient(clientData) {
    const response = await fetch(`${API_BASE_URL}/api/clients`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(clientData)),
    });
    return await handleResponse(response);
  },

  async updateClient(id, clientData) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(clientData)),
    });
    return await handleResponse(response);
  },

  async resetClientPassword(id, newPassword) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${id}/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ new_password: newPassword }),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async deleteClient(id) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async deactivateClient(id) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${id}/deactivate`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async activateClient(id) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${id}/activate`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  // Sites
  async getSites() {
    const response = await fetch(`${API_BASE_URL}/api/sites`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getSitesByClient(clientId) {
    const response = await fetch(`${API_BASE_URL}/api/clients/${clientId}/sites`, {
      headers: getAuthHeaders(),
      cache: 'no-store',
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getSite(id) {
    const response = await fetch(`${API_BASE_URL}/api/sites/${id}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async createSite(siteData) {
    const response = await fetch(`${API_BASE_URL}/api/sites`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(siteData)),
    });
    return await handleResponse(response);
  },

  async updateSite(id, siteData) {
    const response = await fetch(`${API_BASE_URL}/api/sites/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(siteData)),
    });
    return await handleResponse(response);
  },

  async deleteSite(id, force = false) {
    const url = force 
      ? `${API_BASE_URL}/api/sites/${id}?force=true`
      : `${API_BASE_URL}/api/sites/${id}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  // Equipment
  async getEquipment(clientId = null, siteId = null) {
    const response = await fetch(
      buildQueryUrl('/api/equipment', { clientId, siteId }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getEquipmentById(id) {
    const response = await fetch(`${API_BASE_URL}/api/equipment/${id}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async createEquipment(equipmentData) {
    const response = await fetch(`${API_BASE_URL}/api/equipment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(equipmentData)),
    });
    return await handleResponse(response);
  },

  async updateEquipment(id, equipmentData) {
    const response = await fetch(`${API_BASE_URL}/api/equipment/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(equipmentData)),
    });
    return await handleResponse(response);
  },

  async deleteEquipment(id) {
    const response = await fetch(`${API_BASE_URL}/api/equipment/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  // Equipment Placements
  async getPlacements(planId) {
    const response = await fetch(`${API_BASE_URL}/api/placements/plan/${planId}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async createPlacement(placementData) {
    const response = await fetch(`${API_BASE_URL}/api/placements`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(placementData)),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async updatePlacement(id, placementData) {
    const response = await fetch(`${API_BASE_URL}/api/placements/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(placementData)),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async deletePlacement(id) {
    const response = await fetch(`${API_BASE_URL}/api/placements/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  // Plans
  async uploadPlan(clientId, file, name, location, floor, isDefault) {
    const formData = new FormData();
    formData.append('plan', file);
    formData.append('clientId', clientId);
    formData.append('name', name);
    formData.append('location', location || '');
    formData.append('floor', floor || '');
    formData.append('isDefault', isDefault ? 'true' : 'false');

    const token = Cookies.get('petrofeu_token');
    const response = await fetch(`${API_BASE_URL}/api/plans/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async getMyPlans() {
    const response = await fetch(`${API_BASE_URL}/api/plans/my`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getClientPlans(clientId) {
    const response = await fetch(`${API_BASE_URL}/api/plans/client/${clientId}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  planFilePlaceholder: '__no_file__',

  getPlanFileName(plan) {
    if (!plan) return null;
    const direct = plan.fileName || plan.file_name;
    if (direct) {
      if (direct === this.planFilePlaceholder) return null;
      return direct;
    }
    const url = plan.fileUrl || plan.file_url;
    if (!url) return null;
    const segment = url.split('/').pop()?.split('?')[0];
    if (!segment || segment === this.planFilePlaceholder) return null;
    return decodeURIComponent(segment);
  },

  planHasFile(plan) {
    return Boolean(this.getPlanFileName(plan));
  },

  guessPlanContentType(fileName, fallback = 'application/octet-stream') {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    const map = {
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      pdf: 'application/pdf',
      svg: 'image/svg+xml',
    };
    return map[ext] || fallback;
  },

  async fetchPlanFile(plan) {
    const fileName = this.getPlanFileName(plan);
    if (!fileName) {
      throw new Error(
        'Ce plan n’a pas de fichier joint. Téléversez une image ou un PDF depuis Gestion des plans.'
      );
    }

    const token = Cookies.get('petrofeu_token');
    const response = await fetch(
      `${API_BASE_URL}/api/plans/file/${encodeURIComponent(fileName)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    if (!response.ok) {
      const err = await parseJsonSafe(response);
      throw new ApiError(err.message || err.error || 'Impossible de charger le fichier du plan', {
        status: response.status,
        body: err,
      });
    }

    const rawBlob = await response.blob();
    const headerType = response.headers.get('Content-Type')?.split(';')[0]?.trim() || '';
    const contentType =
      rawBlob.type && rawBlob.type !== 'application/octet-stream'
        ? rawBlob.type
        : headerType || this.guessPlanContentType(fileName);
    const blob =
      rawBlob.type === contentType
        ? rawBlob
        : new Blob([rawBlob], { type: contentType });

    return {
      blob,
      blobUrl: URL.createObjectURL(blob),
      contentType,
      fileName,
    };
  },

  async fetchPlanFileBlob(plan) {
    const { blobUrl } = await this.fetchPlanFile(plan);
    return blobUrl;
  },

  async getPlans() {
    const response = await fetch(`${API_BASE_URL}/api/plans`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getPlan(planId) {
    const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async deletePlan(planId) {
    const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async updatePlan(planId, planData) {
    const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(planData)),
    });
    return await handleResponse(response);
  },

  async replacePlanFile(planId, file) {
    const formData = new FormData();
    formData.append('plan', file);

    const token = Cookies.get('petrofeu_token');
    const response = await fetch(`${API_BASE_URL}/api/plans/${planId}/file`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result));
  },

  async deletePlanFile(planId) {
    const response = await fetch(`${API_BASE_URL}/api/plans/${planId}/file`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async setDefaultPlan(planId) {
    const response = await fetch(`${API_BASE_URL}/api/plans/${planId}/set-default`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async updateEquipmentPosition(equipmentId, x, y) {
    const response = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/position`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ x, y }),
    });
    return await handleResponse(response);
  },

  // Maintenance
  async getMaintenances(clientId = null) {
    const response = await fetch(
      buildQueryUrl('/api/maintenance', { clientId }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async createMaintenance(maintenanceData) {
    const response = await fetch(`${API_BASE_URL}/api/maintenance`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(maintenanceData)),
    });
    return await handleResponse(response);
  },

  async updateMaintenance(id, maintenanceData) {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(maintenanceData)),
    });
    return await handleResponse(response);
  },

  async deleteMaintenance(id) {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async getMaintenanceSchedule(clientId = null) {
    const response = await fetch(
      buildQueryUrl('/api/maintenance-schedule', { clientId }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  // Statistics
  async getStatistics(clientId = null) {
    const response = await fetch(
      buildQueryUrl('/api/statistics', { clientId }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result, {}));
  },

  // Reports
  async getReports(clientId = null) {
    const response = await fetch(
      buildQueryUrl('/api/reports', { clientId }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async generateReport(clientId, reportType, startDate = null, endDate = null) {
    const payload = { clientId, reportType };
    if (startDate) payload.startDate = startDate;
    if (endDate) payload.endDate = endDate;
    
    const response = await fetch(`${API_BASE_URL}/api/reports/generate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(payload)),
    });
    return await handleResponse(response);
  },

  async deleteReport(reportId) {
    const response = await fetch(`${API_BASE_URL}/api/reports/${reportId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  // Technical Admin APIs
  async getCabinetPlacements(clientId = null) {
    const response = await fetch(
      buildQueryUrl('/api/cabinet-placements', { clientId }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async createCabinetPlacement(data) {
    const response = await fetch(`${API_BASE_URL}/api/cabinet-placements`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(data)),
    });
    return await handleResponse(response);
  },

  async updateCabinetPlacement(id, data) {
    const response = await fetch(`${API_BASE_URL}/api/cabinet-placements/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(toSnakeCase(data)),
    });
    return await handleResponse(response);
  },

  async deleteCabinetPlacement(id) {
    const response = await fetch(`${API_BASE_URL}/api/cabinet-placements/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  async getVehicleEquipment(clientId = null, structure = null) {
    const response = await fetch(
      buildQueryUrl('/api/vehicle-equipment', { clientId, structure }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  async getExtinguisherInventory(clientId = null, location = null, status = null) {
    const response = await fetch(
      buildQueryUrl('/api/extinguisher-inventory', { clientId, location, status }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    return toCamelCase(unwrapList(result));
  },

  // Contact form submissions (website messages)
  async getContactSubmissions(status = null, page = 1, limit = 50) {
    const response = await fetch(
      buildQueryUrl('/api/contact', { page, limit, status }),
      { headers: getAuthHeaders() }
    );
    const result = await handleResponse(response);
    const data = unwrapData(result, {});
    return {
      submissions: toCamelCase(data.submissions || []),
      pagination: toCamelCase(data.pagination || {}),
    };
  },

  async updateContactSubmission(id, status, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/contact/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, notes }),
    });
    return await handleResponse(response);
  },

  async deleteContactSubmission(id) {
    const response = await fetch(`${API_BASE_URL}/api/contact/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  // Database backups
  async listBackups() {
    const response = await fetch(`${API_BASE_URL}/api/admin/backups`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result, {}));
  },

  async createBackup() {
    const response = await fetch(`${API_BASE_URL}/api/admin/backups`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result, {}));
  },

  async restoreBackup(backupKey) {
    const response = await fetch(`${API_BASE_URL}/api/admin/backups/restore`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ backupKey }),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result, {}));
  },

  async deleteBackup(backupKey) {
    const response = await fetch(`${API_BASE_URL}/api/admin/backups/delete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ backupKey }),
    });
    return await handleResponse(response);
  },

  async updateBackupSettings(frequency, enabled = true) {
    const response = await fetch(`${API_BASE_URL}/api/admin/backups/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ frequency, enabled }),
    });
    const result = await handleResponse(response);
    return toCamelCase(unwrapData(result, {}));
  },

  async downloadBackup(backupKey) {
    const token = Cookies.get('petrofeu_token');
    const url = `${API_BASE_URL}/api/admin/backups/download?backupKey=${encodeURIComponent(backupKey)}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const err = await parseJsonSafe(response);
      throw new ApiError(err.message || err.error || 'Téléchargement échoué', {
        status: response.status,
        body: err,
      });
    }
    const blob = await response.blob();
    const fileName = `petrofeu-backup-${Date.now()}.json`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  },
};

